import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SvgPreview from './SvgPreview';

const DEF = { enabled: true, unit: 'in', width: 4, height: 2, cols: 1, rows: 1, margin: 0, gap: 0, fit: true };
const NUM = ['width', 'height', 'cols', 'rows', 'margin', 'gap'];

// Per-template sheet stock layout (N-up). Used for the PDF and the combined engraver SVG sheets.
export const SheetLayoutPanel = ({ api, template, onSaved }) => {
  const [s, setS] = useState({ ...DEF, ...(template.sheet || {}) });
  const [ver, setVer] = useState(0);
  useEffect(() => { setS({ ...DEF, ...(template.sheet || {}) }); }, [template.id]);
  const num = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const body = () => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, NUM.includes(k) ? Number(v) : v]));
  const b = body();
  const valid = b.width > 0 && b.height > 0 && b.cols >= 1 && b.rows >= 1 && b.width - 2 * b.margin - (b.cols - 1) * b.gap > 0 && b.height - 2 * b.margin - (b.rows - 1) * b.gap > 0;
  const save = async () => {
    try { const r = await api().put(`/faceplates/${template.id}/sheet`, body()); toast.success('Sheet layout saved'); onSaved(r.data); setVer(v => v + 1); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const field = (k, label, step = '0.01') => (
    <div><Label className="text-xs">{label}</Label><Input type="number" step={step} min="0" value={s[k]} onChange={num(k)} className="h-8 rounded-sm mono" data-testid={`sheet-${k}-input`} /></div>
  );
  return (
    <div className="space-y-2 border border-slate-200 rounded-sm p-3" data-testid="sheet-layout-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Sheet layout (print stock)</p>
        <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="sheet-enabled-checkbox" /> Use sheet layout for PDF and engraver sheets</label>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2 items-end">
        <div><Label className="text-xs">Unit</Label>
          <select value={s.unit} onChange={(e) => setS({ ...s, unit: e.target.value })} className="h-8 w-full border border-slate-200 rounded-sm text-xs px-1" data-testid="sheet-unit-select"><option value="in">in</option><option value="mm">mm</option></select></div>
        {field('width', 'Sheet W')}{field('height', 'Sheet H')}{field('cols', 'Columns', '1')}{field('rows', 'Rows', '1')}{field('margin', 'Margin')}{field('gap', 'Gap')}
        <label className="text-xs flex items-center gap-1.5 pb-2"><input type="checkbox" checked={s.fit} onChange={(e) => setS({ ...s, fit: e.target.checked })} data-testid="sheet-fit-checkbox" /> Scale to fit</label>
      </div>
      <p className="text-[11px] text-slate-500">{valid ? b.cols * b.rows : '—'} faceplate(s) per sheet. Turn off "Scale to fit" to print at the design's real size (centered, cropped to the cell).</p>
      {!valid ? <p className="text-xs text-red-600" data-testid="sheet-invalid">Enter a sheet size, at least 1 row and column, and margins/gaps that leave room for the faceplates.</p> : <SvgPreview api={api} url={`/faceplates/${template.id}/sheet-preview`} method="post" body={body()} refreshKey={`${ver}-${JSON.stringify(s)}`} className="max-h-64" testId="sheet-preview" />}
      <Button size="sm" onClick={save} disabled={!valid} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="sheet-save-btn">Save sheet layout</Button>
    </div>
  );
};

export default SheetLayoutPanel;
